import useSWR, { type SWRResponse } from "swr";
import {
  type Agent,
  type AgentList,
  AgentListView,
  agentListPath,
  agentsByIdsPath,
  enabledAgentsPath,
  fetchAgentsByIds,
} from "~/services/agents";
import { apiClient } from "~/utils/apiClient";
import { fetchAllPages } from "~/utils/fetchAllPages";

/** The backend caps a single agents page at 100 (its `MAX_LIMIT`). */
const AGENT_PAGE_SIZE = 100;

/**
 * Fetch every agent the caller can see by walking the offset/limit pages that
 * `GET /agents` returns, following `metadata.has_more` until the set is
 * exhausted. The endpoint defaults to a 50-item page and never returns more than
 * 100 at once, so a single request silently hides any agent past that window
 * (agents are ordered by `name` ASC).
 */
export async function fetchAllAgents(baseUrl: string): Promise<AgentList> {
  return fetchAllPages<Agent>(baseUrl, AGENT_PAGE_SIZE);
}

/**
 * All agents visible to the caller in the given `view`, fully paged. Shares the
 * `agentListPath({ view })` SWR key so `mutateAgentList()` after a
 * create/update/delete revalidates the whole list (every view). Pass `enabled`
 * `false` to skip fetching (e.g. a dialog that only needs the list while open).
 */
export function useAgentList(
  enabled: boolean = true,
  view: AgentListView = AgentListView.USABLE
): SWRResponse<AgentList> {
  return useSWR(enabled ? agentListPath({ view }) : null, fetchAllAgents);
}

/**
 * Resolve a set of agent ids to their records in one `POST /agents/by-ids`, for
 * naming ids a record already refers to rather than browsing. Reaches agents
 * outside the caller's `usable` set, which an `admin:agents` reader needs, and
 * omits ids that are DELETED or not viewable — a missing id is gone, not an error.
 *
 * Keyed on the sorted, de-duplicated ids, so two callers share one request.
 */
export function useAgentsByIds(ids: string[]): SWRResponse<Agent[]> {
  const unique = [...new Set(ids)].sort();
  return useSWR(unique.length > 0 ? [agentsByIdsPath(), ...unique] : null, () =>
    fetchAgentsByIds(unique)
  );
}

/** Default cap for a filter dropdown's agent list - enough to cover the live
 *  agents on a pod without letting the menu grow unbounded. */
export const ENABLED_AGENTS_LIMIT = 20;

/**
 * A single, capped page of ENABLED agents for filter dropdowns. Unlike
 * `useAgentList`, this neither walks every page nor includes DISABLED drafts (the
 * unnamed "New Agent" rows a pod accumulates): the backend filters by
 * `status=ENABLED` and returns at most `limit` rows, ordered by `display_name`.
 * Pagination is intentionally omitted - the filter is a convenience, not an
 * exhaustive index. Shares the `${AGENTS_API_PREFIX}?…` key space, so
 * `mutateAgentList()` after a create/enable/disable revalidates it too.
 */
export function useEnabledAgents(
  view: AgentListView = AgentListView.USABLE,
  limit: number = ENABLED_AGENTS_LIMIT
): SWRResponse<AgentList> {
  return useSWR(enabledAgentsPath({ view, limit }), (url: string) => apiClient<AgentList>(url));
}
